import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { BeakerIcon, PlusIcon } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '@/components/core/button';
import {
  DataTable,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  useDataTable,
  useDataTableState,
  useRowNavigationCallback,
} from '@/components/data-table';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { useGateways } from '@/gateways/GatewayProvider';
import type { SimulationDTO } from '@/gateways/impl/SimulationGateway';
import { formatDateTime } from '@/shared/helpers';
import { simulationInputsStatusType } from '@/shared/status-maps';
import { simulationInputsStatusTitle } from '@/shared/title-maps';

const columnHelper = createColumnHelper<SimulationDTO>();

const columns = [
  columnHelper.accessor('name', { header: 'Name' }),
  columnHelper.accessor('type', { header: 'Type' }),
  columnHelper.accessor('inputsStatus', {
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue();
      return (
        <StatusBadge
          status={status}
          type={simulationInputsStatusType}
          title={simulationInputsStatusTitle}
        />
      );
    },
  }),
  columnHelper.accessor('createdBy', { header: 'Created by' }),
  columnHelper.accessor('createdAt', {
    header: 'Created',
    cell: ({ getValue }) => formatDateTime(getValue()),
  }),
];

export function SimulationList() {
  const { simulationGateway } = useGateways();
  const onClickRow = useRowNavigationCallback<SimulationDTO>('/simulations');

  const { state, onPaginationChange, onSortingChange } = useDataTableState({
    columns,
  });

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['simulations'],
    queryFn: ({ signal }) => simulationGateway.getSimulations({ signal }),
  });

  const table = useDataTable({
    data,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && !!data,
    hasError: isError,
    manualPagination: false,
    manualSorting: false,
    autoResetPageIndex: false,
    onPaginationChange,
    onSortingChange,
    state,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <BeakerIcon />
        </HeaderIcon>
        <HeaderTitle>Simulations</HeaderTitle>
        <HeaderActions>
          <Button asChild>
            <Link to="/simulations/new">
              <PlusIcon />
              New simulation
            </Link>
          </Button>
        </HeaderActions>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableRefreshMenu onRefresh={refetch} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
